import { useState } from 'react';
import { IonAlert, IonContent, IonModal, IonSpinner, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { useAddressCapture } from '../../hooks/useAddressCapture';
import { useLocationStore } from '../../store/useLocationStore';
import { LocaleSearchInput } from './LocaleSearchInput';

interface AddressCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * When provided, shows a one-tap "Use dish location" option that sets the
   * user's address from these coords (reverse-geocoded). Used by the order
   * gate so a share-link visitor can adopt the listing's location instantly.
   */
  dishCoords?: { lat: number; lon: number } | null;
  /**
   * The dish's locale id + display name (order gate only). The name labels the
   * "Use dish location" button; the id gates the search path — entering a city
   * or ZIP in a different locale prompts a confirmation before the address is
   * committed.
   */
  dishLocaleId?: string | null;
  dishLocaleName?: string | null;
  /**
   * Fired when an address is successfully set (any path). The order gate uses
   * this to resume the order; when omitted the modal just closes on success.
   */
  onLocationSet?: () => void;
}

/**
 * Address-capture modal opened from the order gate — confirms the eater's
 * location and saves it to their PROFILE (home) address before ordering.
 *
 * Offers exactly one one-tap option plus a city/ZIP search fallback:
 *  - if the user has a current browse location (resolved address or operating
 *    locale), adopt that; otherwise (share-link visitor who bypassed the home
 *    gate) adopt the listing's own location.
 *  - or search a city / type a ZIP.
 *
 * Whichever path is taken, the underlying hook persists a `type:'profile'`
 * address (`mirrorMode: 'profileAddress'`) — postal-level is enough for the
 * order gate; publish prompts separately for a full street.
 *
 * For a browse locale change that must NOT touch the profile (the TopBar pill)
 * use `LocaleSelectorModal` instead.
 */
export const AddressCaptureModal: React.FC<AddressCaptureModalProps> = ({
  isOpen,
  onClose,
  dishCoords,
  dishLocaleId,
  dishLocaleName,
  onLocationSet,
}) => {
  const { t } = useTranslation('location');
  const currentLocation = useLocationStore((s) => s.location);
  const browseLocale = useLocationStore((s) => s.locale);

  // Open when the entered location resolves to a locale other than the dish's —
  // the user must confirm they know the dish is elsewhere before we commit.
  const [mismatchOpen, setMismatchOpen] = useState(false);

  const {
    query,
    setQuery,
    searchError,
    coordsLoading,
    coordsError,
    captureFromCoords,
    handleSearchSubmit,
    confirmPendingSearch,
    cancelPendingSearch,
    searchSubmitting,
  } = useAddressCapture({
    expectedLocaleId: dishLocaleId,
    onSearchLocaleMismatch: () => setMismatchOpen(true),
    onCaptured: () => (onLocationSet ? onLocationSet() : onClose()),
    // The order gate IS the "set your profile/tax address" surface, so a
    // street-less capture (city, ZIP, or the adopted coords) is saved as a
    // partial `type:'profile'` address.
    mirrorMode: 'profileAddress',
  });

  // The user's current BROWSE location, if any. Prefer their resolved address
  // coords; fall back to the operating locale's centroid (a TopBar-ZIP browser
  // has a `locale` but no `location`). Null only when they reached this listing
  // without ever setting a browse location — e.g. a share link that bypassed the
  // home-feed gate; in that case we offer the listing's own location instead.
  const browseCoords =
    currentLocation?.lat != null && currentLocation?.lon != null
      ? { lat: currentLocation.lat, lon: currentLocation.lon }
      : browseLocale?.lat != null && browseLocale?.lon != null
        ? { lat: browseLocale.lat, lon: browseLocale.lon }
        : null;

  const browseLabel =
    currentLocation?.city && currentLocation?.stateProvince
      ? `${currentLocation.city}, ${currentLocation.stateProvince}`
      : currentLocation?.postalCode || browseLocale?.name || t('selector.unknownLocation');

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          {/* Always "set", never "change": this gate only opens when the
              PROFILE has no orderable address (see ListingDetailsPage's
              proceedToOrder), so the user has never set one. The old title
              keyed off the browse location, which is a different concept and
              read as "change" to anyone who had browsed anywhere. */}
          <IonTitle>{t('selector.setLocationTitle')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>{t('common:actions.close')}</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="mx-auto flex w-full max-w-md flex-col gap-6 px-6 py-8">
          <p className="text-center text-base font-medium">{t('selector.orderIntro')}</p>

          {/* Exactly one adopt-this-location option: the user's current browse
              location when we have one, otherwise the listing's own location
              (the share-link case, where the browse gate was bypassed). */}
          {browseCoords ? (
            <div className="space-y-2">
              <p className="text-center text-sm text-muted-foreground">
                {t('selector.browseLocationLabel', { location: browseLabel })}
              </p>
              <button
                type="button"
                onClick={() => captureFromCoords(browseCoords.lat, browseCoords.lon)}
                disabled={coordsLoading}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-primary py-3 text-base font-semibold text-white active:opacity-80 disabled:opacity-50"
              >
                {coordsLoading ? (
                  <>
                    <IonSpinner name="crescent" className="h-5 w-5" />
                    {t('gettingLocation')}
                  </>
                ) : (
                  t('selector.useBrowseLocation')
                )}
              </button>
              {coordsError && <p className="text-center text-sm text-brand-danger">{coordsError}</p>}
            </div>
          ) : dishCoords ? (
            <div className="space-y-2">
              {dishLocaleName && (
                <p className="text-center text-sm text-muted-foreground">
                  {t('selector.dishLocationLabel', { locale: dishLocaleName })}
                </p>
              )}
              <button
                type="button"
                onClick={() => captureFromCoords(dishCoords.lat, dishCoords.lon)}
                disabled={coordsLoading}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-primary py-3 text-base font-semibold text-white active:opacity-80 disabled:opacity-50"
              >
                {coordsLoading ? (
                  <>
                    <IonSpinner name="crescent" className="h-5 w-5" />
                    {t('gettingLocation')}
                  </>
                ) : (
                  t('selector.useDishLocation')
                )}
              </button>
              {coordsError && <p className="text-center text-sm text-brand-danger">{coordsError}</p>}
            </div>
          ) : null}

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs uppercase tracking-wide text-muted-foreground">{t('divider.or')}</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <LocaleSearchInput
            inputId="change-location-search"
            value={query}
            onChange={setQuery}
            onSubmit={handleSearchSubmit}
            submitting={searchSubmitting}
            error={searchError}
            label={t('selector.searchLabelChange')}
            placeholder={t('search.placeholder')}
            ctaLabel={t('selector.updateCta')}
          />
        </div>
      </IonContent>
      <IonAlert
        isOpen={mismatchOpen}
        onDidDismiss={() => setMismatchOpen(false)}
        header={t('selector.mismatchTitle')}
        message={t('selector.mismatchMessage', {
          locale: dishLocaleName ?? t('selector.unknownLocation'),
        })}
        buttons={[
          { text: t('common:actions.cancel'), role: 'cancel', handler: () => cancelPendingSearch() },
          { text: t('common:actions.confirm'), handler: () => confirmPendingSearch() },
        ]}
      />
    </IonModal>
  );
};
